/**
 * ProductCard Component
 * Product preview card for listings
 * BP-08-04: Marketplace Frontend (Family)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Shield, ImageIcon, Sparkles } from 'lucide-react';
import { StarRating } from './StarRating';
import { PriceDisplay } from './PriceDisplay';
import type { ProductCardProps } from '@/features/marketplace/types/marketplaceTypes';

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  variant = 'default',
  showVendor = true,
  onWishlistToggle,
  isWishlisted = false,
}) => {
  const {
    slug,
    name,
    shortDescription,
    imageUrls,
    priceMin,
    priceMax,
    priceType,
    acceptsInsurance,
    avgRating,
    reviewCount,
    vendor,
    isFeatured,
  } = product;
  
  const primaryImage = imageUrls[0] || null;
  
  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onWishlistToggle?.(product.id);
  };
  
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/marketplace/products/${slug}`}
        className="
          group flex gap-4 p-4
          bg-white rounded-xl
          border border-warm-gray/10
          hover:border-sage/30 hover:shadow-medium
          transition-all duration-300
        "
      >
        {/* Image */}
        <div className="
          relative w-32 h-32 rounded-lg bg-cream-dark
          flex items-center justify-center flex-shrink-0
          overflow-hidden
        ">
          {primaryImage ? (
            <img
              src={primaryImage}
              alt={name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <ImageIcon className="w-8 h-8 text-warm-gray" />
          )}
          
          {isFeatured && (
            <div className="
              absolute top-2 left-2
              flex items-center gap-1
              bg-terracotta text-white
              px-2 py-0.5 rounded-full
              text-xs font-medium
            ">
              <Sparkles className="w-3 h-3" />
              Featured
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="
                font-semibold text-navy truncate
                group-hover:text-sage-dark transition-colors
              ">
                {name}
              </h3>
              
              {showVendor && vendor && (
                <p className="text-sm text-warm-gray truncate">
                  by {vendor.name}
                </p>
              )}
            </div>
            
            {onWishlistToggle && (
              <button
                onClick={handleWishlistClick}
                className={`
                  p-1.5 rounded-full flex-shrink-0
                  transition-colors duration-200
                  ${isWishlisted 
                    ? 'text-terracotta bg-terracotta/10' 
                    : 'text-warm-gray hover:text-terracotta hover:bg-terracotta/10'
                  }
                `}
                aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <Heart
                  className="w-5 h-5"
                  fill={isWishlisted ? 'currentColor' : 'none'}
                />
              </button>
            )}
          </div>
          
          {avgRating !== null && (
            <div className="flex items-center gap-2 mt-1">
              <StarRating rating={avgRating} size="sm" />
              <span className="text-xs text-warm-gray">({reviewCount})</span>
            </div>
          )}
          
          <div className="mt-auto pt-2">
            <PriceDisplay
              priceMin={priceMin}
              priceMax={priceMax}
              priceType={priceType}
              size="sm"
            />
          </div>
        </div>
      </Link>
    );
  }
  
  if (variant === 'compact') {
    return (
      <Link
        to={`/marketplace/products/${slug}`}
        className="
          group block bg-white rounded-lg
          border border-warm-gray/10
          overflow-hidden
          hover:border-sage/30 hover:shadow-soft
          transition-all duration-200
        "
      >
        <div className="
          relative aspect-square bg-cream-dark
          flex items-center justify-center
          overflow-hidden
        ">
          {primaryImage ? (
            <img
              src={primaryImage}
              alt={name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <ImageIcon className="w-8 h-8 text-warm-gray" />
          )}
        </div>
        
        <div className="p-3">
          <h3 className="font-medium text-sm text-navy line-clamp-1">{name}</h3>
          <PriceDisplay
            priceMin={priceMin}
            priceMax={priceMax}
            priceType={priceType}
            size="sm"
          />
        </div>
      </Link>
    );
  }
  
  // Default variant
  return (
    <Link
      to={`/marketplace/products/${slug}`}
      className="
        group block bg-white rounded-xl
        border border-warm-gray/10
        overflow-hidden
        hover:border-sage/30 hover:shadow-medium
        transition-all duration-300
      "
    >
      {/* Image */}
      <div className="
        relative aspect-[4/3] bg-cream-dark
        flex items-center justify-center
        overflow-hidden
      ">
        {primaryImage ? (
          <img
            src={primaryImage}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <ImageIcon className="w-12 h-12 text-warm-gray" />
        )}
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {isFeatured && (
            <div className="
              flex items-center gap-1
              bg-terracotta text-white
              px-2.5 py-1 rounded-full
              text-xs font-medium
            ">
              <Sparkles className="w-3 h-3" />
              Featured
            </div>
          )}
          
          {acceptsInsurance && (
            <div className="
              flex items-center gap-1
              bg-white/90 backdrop-blur-sm text-sage-dark
              px-2.5 py-1 rounded-full
              text-xs font-medium
            ">
              <Shield className="w-3 h-3" />
              Insurance
            </div>
          )}
        </div>
        
        {/* Wishlist button */}
        {onWishlistToggle && (
          <button
            onClick={handleWishlistClick}
            className={`
              absolute top-3 right-3
              p-2 rounded-full
              bg-white/90 backdrop-blur-sm
              transition-colors duration-200
              ${isWishlisted 
                ? 'text-terracotta' 
                : 'text-warm-gray hover:text-terracotta'
              }
            `}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              className="w-5 h-5"
              fill={isWishlisted ? 'currentColor' : 'none'}
            />
          </button>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        {/* Vendor */}
        {showVendor && vendor && (
          <p className="text-xs font-medium text-sage-dark mb-1">
            {vendor.name}
          </p>
        )}
        
        {/* Name */}
        <h3 className="
          font-display font-semibold text-navy
          line-clamp-2 mb-2
          group-hover:text-sage-dark transition-colors
        ">
          {name}
        </h3>
        
        {/* Description */}
        {shortDescription && (
          <p className="text-sm text-warm-gray line-clamp-2 mb-3">
            {shortDescription}
          </p>
        )}
        
        {/* Rating */}
        {avgRating !== null && (
          <div className="flex items-center gap-2 mb-3">
            <StarRating rating={avgRating} size="sm" />
            <span className="text-sm text-warm-gray">
              {avgRating.toFixed(1)} ({reviewCount})
            </span>
          </div>
        )}
        
        {/* Price */}
        <PriceDisplay
          priceMin={priceMin}
          priceMax={priceMax}
          priceType={priceType}
          size="md"
        />
      </div>
    </Link>
  );
};

export default ProductCard;

